import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  databaseAnalyses: [],
  databaseTypes: [], // From /database-analyses/init
  databaseStatuses: [], // From /database-analyses/init
  units: [], // From /database-analyses/init
  selectedDatabaseAnalysis: null,
  loading: false,
  error: null,
  successMessage: null,
  // Pagination
  page: 1,
  pageSize: 10,
  total: 0,
  // Sorting
  sortColumn: 'lastSyncTime',
  sortDirection: 'desc',
  // Filtering
  filterName: '',
  filterCode: '',
  filterUnitId: [],
  filterDatabaseType: [],
  filterStatus: [],
  filterLastSyncFrom: null,
  filterLastSyncTo: null,
  filterLastQueryFrom: null,
  filterLastQueryTo: null,
  filterMinUsagePercentage: null,
  filterMaxUsagePercentage: null,
  filterMinTotalQueries: null,
  filterMaxTotalQueries: null,
};

const databaseAnalysisSlice = createSlice({
  name: 'databaseAnalysis',
  initialState,
  reducers: {
    // Fetch init data (database types, health statuses, units, etc.)
    fetchInit: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInitSuccess: (state, action) => {
      state.loading = false;
      state.databaseTypes = action.payload.databaseTypes || [];
      state.databaseStatuses = action.payload.databaseStatuses || [];
      state.units = action.payload.units || [];
      state.error = null;
    },
    fetchInitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch database analyses
    fetchDatabaseAnalyses: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchDatabaseAnalysesSuccess: (state, action) => {
      state.loading = false;
      state.databaseAnalyses = action.payload.data;
      state.total = action.payload.meta.total;
      state.error = null;
    },
    fetchDatabaseAnalysesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch database analysis by ID
    fetchDatabaseAnalysisById: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchDatabaseAnalysisByIdSuccess: (state, action) => {
      state.loading = false;
      state.selectedDatabaseAnalysis = action.payload;
      state.error = null;
    },
    fetchDatabaseAnalysisByIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Add database analysis
    addDatabaseAnalysis: (state) => {
      state.loading = true;
      state.error = null;
    },
    addDatabaseAnalysisSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    addDatabaseAnalysisFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update database analysis
    updateDatabaseAnalysis: (state) => {
      state.loading = true;
      state.error = null;
    },
    updateDatabaseAnalysisSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    updateDatabaseAnalysisFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Delete database analysis
    deleteDatabaseAnalysis: (state) => {
      state.loading = true;
      state.error = null;
    },
    deleteDatabaseAnalysisSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    deleteDatabaseAnalysisFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Bulk delete
    bulkDeleteDatabaseAnalyses: (state) => {
      state.loading = true;
      state.error = null;
    },
    bulkDeleteDatabaseAnalysesSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    bulkDeleteDatabaseAnalysesFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Export to Excel
    exportDatabaseAnalysesToExcel: (state) => {
      state.loading = true;
      state.error = null;
    },
    exportDatabaseAnalysesToExcelSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    exportDatabaseAnalysesToExcelFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Export to PDF
    exportDatabaseAnalysesToPdf: (state) => {
      state.loading = true;
      state.error = null;
    },
    exportDatabaseAnalysesToPdfSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    exportDatabaseAnalysesToPdfFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Export usage report to Excel
    exportDatabaseUsageReportToExcel: (state) => {
      state.loading = true;
      state.error = null;
    },
    exportDatabaseUsageReportToExcelSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    exportDatabaseUsageReportToExcelFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Export usage report to PDF
    exportDatabaseUsageReportToPdf: (state) => {
      state.loading = true;
      state.error = null;
    },
    exportDatabaseUsageReportToPdfSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    exportDatabaseUsageReportToPdfFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1; // Reset to first page
    },

    // Sorting
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },

    // Filtering
    setFilterName: (state, action) => {
      state.filterName = action.payload;
      state.page = 1; // Reset to first page when filtering
    },
    setFilterCode: (state, action) => {
      state.filterCode = action.payload;
      state.page = 1;
    },
    setFilterUnitId: (state, action) => {
      state.filterUnitId = action.payload;
      state.page = 1;
    },
    setFilterDatabaseType: (state, action) => {
      state.filterDatabaseType = action.payload;
      state.page = 1;
    },
    setFilterStatus: (state, action) => {
      state.filterStatus = action.payload;
      state.page = 1;
    },
    setFilterLastSyncFrom: (state, action) => {
      state.filterLastSyncFrom = action.payload;
      state.page = 1;
    },
    setFilterLastSyncTo: (state, action) => {
      state.filterLastSyncTo = action.payload;
      state.page = 1;
    },
    setFilterLastQueryFrom: (state, action) => {
      state.filterLastQueryFrom = action.payload;
      state.page = 1;
    },
    setFilterLastQueryTo: (state, action) => {
      state.filterLastQueryTo = action.payload;
      state.page = 1;
    },
    setFilterMinUsagePercentage: (state, action) => {
      state.filterMinUsagePercentage = action.payload;
      state.page = 1;
    },
    setFilterMaxUsagePercentage: (state, action) => {
      state.filterMaxUsagePercentage = action.payload;
      state.page = 1;
    },
    setFilterMinTotalQueries: (state, action) => {
      state.filterMinTotalQueries = action.payload;
      state.page = 1;
    },
    setFilterMaxTotalQueries: (state, action) => {
      state.filterMaxTotalQueries = action.payload;
      state.page = 1;
    },

    // Clear messages
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
  },
});

export const {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchDatabaseAnalyses,
  fetchDatabaseAnalysesSuccess,
  fetchDatabaseAnalysesFailure,
  fetchDatabaseAnalysisById,
  fetchDatabaseAnalysisByIdSuccess,
  fetchDatabaseAnalysisByIdFailure,
  addDatabaseAnalysis,
  addDatabaseAnalysisSuccess,
  addDatabaseAnalysisFailure,
  updateDatabaseAnalysis,
  updateDatabaseAnalysisSuccess,
  updateDatabaseAnalysisFailure,
  deleteDatabaseAnalysis,
  deleteDatabaseAnalysisSuccess,
  deleteDatabaseAnalysisFailure,
  bulkDeleteDatabaseAnalyses,
  bulkDeleteDatabaseAnalysesSuccess,
  bulkDeleteDatabaseAnalysesFailure,
  exportDatabaseAnalysesToExcel,
  exportDatabaseAnalysesToExcelSuccess,
  exportDatabaseAnalysesToExcelFailure,
  exportDatabaseAnalysesToPdf,
  exportDatabaseAnalysesToPdfSuccess,
  exportDatabaseAnalysesToPdfFailure,
  exportDatabaseUsageReportToExcel,
  exportDatabaseUsageReportToExcelSuccess,
  exportDatabaseUsageReportToExcelFailure,
  exportDatabaseUsageReportToPdf,
  exportDatabaseUsageReportToPdfSuccess,
  exportDatabaseUsageReportToPdfFailure,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
  setFilterCode,
  setFilterUnitId,
  setFilterDatabaseType,
  setFilterStatus,
  setFilterLastSyncFrom,
  setFilterLastSyncTo,
  setFilterLastQueryFrom,
  setFilterLastQueryTo,
  setFilterMinUsagePercentage,
  setFilterMaxUsagePercentage,
  setFilterMinTotalQueries,
  setFilterMaxTotalQueries,
  clearError,
  clearSuccessMessage,
} = databaseAnalysisSlice.actions;

// Selectors
export const selectDatabaseAnalyses = (state) => state.databaseAnalysis?.databaseAnalyses || [];
export const selectDatabaseTypes = (state) => state.databaseAnalysis?.databaseTypes || [];
export const selectDatabaseStatuses = (state) => state.databaseAnalysis?.databaseStatuses || [];
export const selectUnits = (state) => state.databaseAnalysis?.units || [];
export const selectSelectedDatabaseAnalysis = (state) => state.databaseAnalysis?.selectedDatabaseAnalysis;
export const selectDatabaseAnalysisLoading = (state) => state.databaseAnalysis?.loading || false;
export const selectDatabaseAnalysisError = (state) => state.databaseAnalysis?.error;
export const selectDatabaseAnalysisSuccessMessage = (state) => state.databaseAnalysis?.successMessage;
export const selectDatabaseAnalysisTotal = (state) => state.databaseAnalysis?.total || 0;
export const selectDatabaseAnalysisPage = (state) => state.databaseAnalysis?.page || 1;
export const selectDatabaseAnalysisPageSize = (state) => state.databaseAnalysis?.pageSize || 10;
export const selectDatabaseAnalysisSortColumn = (state) => state.databaseAnalysis?.sortColumn || 'lastSyncTime';
export const selectDatabaseAnalysisSortDirection = (state) => state.databaseAnalysis?.sortDirection || 'desc';
export const selectFilterName = (state) => state.databaseAnalysis?.filterName || '';
export const selectFilterCode = (state) => state.databaseAnalysis?.filterCode || '';
export const selectFilterUnitId = (state) => state.databaseAnalysis?.filterUnitId || [];
export const selectFilterDatabaseType = (state) => state.databaseAnalysis?.filterDatabaseType || [];
export const selectFilterStatus = (state) => state.databaseAnalysis?.filterStatus || [];
export const selectFilterLastSyncFrom = (state) => state.databaseAnalysis?.filterLastSyncFrom;
export const selectFilterLastSyncTo = (state) => state.databaseAnalysis?.filterLastSyncTo;
export const selectFilterLastQueryFrom = (state) => state.databaseAnalysis?.filterLastQueryFrom;
export const selectFilterLastQueryTo = (state) => state.databaseAnalysis?.filterLastQueryTo;
export const selectFilterMinUsagePercentage = (state) => state.databaseAnalysis?.filterMinUsagePercentage;
export const selectFilterMaxUsagePercentage = (state) => state.databaseAnalysis?.filterMaxUsagePercentage;
export const selectFilterMinTotalQueries = (state) => state.databaseAnalysis?.filterMinTotalQueries;
export const selectFilterMaxTotalQueries = (state) => state.databaseAnalysis?.filterMaxTotalQueries;

export default databaseAnalysisSlice.reducer;
